import { useEffect, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { TopBar } from './TopBar';
import { Sidebar } from './Sidebar';
import { BottomNav } from './BottomNav';
import { PostLoginModalBanner } from './PostLoginModalBanner';
import { TopPageBanner } from './TopPageBanner';
import { SubscriptionGateBanner } from './SubscriptionGateBanner';
import { KeyboardShortcutsModal } from '../components/KeyboardShortcutsModal';
import { useKeyboardShortcuts } from '../lib/useKeyboardShortcuts';
import { cn } from '../lib/cn';
import { BrandProvider } from '../brand/BrandProvider';
import { useActiveBrand } from '../api/brand';
import { useActiveBusiness } from '../api/hooks';

/**
 * The application chrome.
 *
 * Layout:
 *   [TopBar — sticky 56px]
 *   [Sidebar 240px] + [Main (flexible)]
 *   [BottomNav (mobile only)]
 *
 * On <lg, the sidebar becomes a drawer triggered by the hamburger in TopBar.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { open: shortcutsOpen, setOpen: setShortcutsOpen } = useKeyboardShortcuts();
  const brand = useActiveBrand();
  const { data: activeBusiness } = useActiveBusiness();

  // Close drawer on resize-up to lg
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setDrawerOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [drawerOpen]);

  return (
    <BrandProvider brand={brand}>
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <TopPageBanner />
      <SubscriptionGateBanner businessId={activeBusiness?.id} />
      <TopBar onOpenMobileNav={() => setDrawerOpen(true)} />

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block sticky top-14 self-start h-[calc(100vh-3.5rem)]">
          <Sidebar />
        </aside>

        {/* Mobile drawer */}
        {drawerOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-zinc-950/40 backdrop-blur-[2px]"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <div className="relative w-72 h-full bg-zinc-50 dark:bg-zinc-900 shadow-lg flex">
              <Sidebar className="border-r-0" />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-md hover:bg-zinc-200 dark:hover:bg-zinc-800 transition"
                aria-label="Close navigation"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Main content */}
        <main className={cn('flex-1 min-w-0 pb-20 lg:pb-0')}>
          {children}
        </main>
      </div>

      <BottomNav />
      <PostLoginModalBanner />
      <KeyboardShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
    </BrandProvider>
  );
}
